import React, { Component } from 'react';
import { withRouter } from 'react-router';
import { Col, Row } from 'reactstrap';
import { get } from 'lodash-es';
import axios from 'axios';

import Step4Dropzone from '../Step4/Step4Dropzone';
import imageUploadErrors from '../Step4/imageUploadErrors';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import { fileDeleteErrorText, offlineErrorText } from '../../../utilities/StaticText';
import AddLotImageContainer from './AddLotImageContainer';

class AddLotDropzone extends Component {
  constructor(props) {
    super(props);
    this.state = {
      files: [],
      fileDeleteError: false,
      isDeletingFile: false,
      lotImages: props.lotImages,
      offlineError: false
    };
  }

  abortFile = index => {
    const files = [...this.state.files];
    files.splice(index, 1);
    this.setState({ files });
  };

  // We need to track errors here so we can display them above the list of files
  addFileError = (code, index) => {
    const {
      match: {
        params: { type }
      }
    } = this.props;
    const files = [...this.state.files];

    if (files[index]) {
      files[index].isComplete = true;
      files[index].hasError = true;
      files[index].error = imageUploadErrors(code, type);
      this.setState({ files });
    }
  };

  handleDeleteFile = async index => {
    const {
      houseID,
      lotData,
      match: {
        params: { catalogID }
      }
    } = this.props;

    // lotNumber in props may contain the extension
    const { lotID } = lotData;
    const lotImageCopy = { ...this.state.lotImages };
    const lotImageKeys = Object.keys(lotImageCopy);

    if (lotImageKeys.indexOf(index.toString()) >= 0) {
      try {
        const postData = {
          catalogID,
          lotID,
          sequence: index,
          imgType: 'lot'
        };

        this.setState({ isDeletingFile: true });
        await axios.post('/cps/app/image/delete', postData, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        });

        // If the delete worked - re-sequence and re-fetch the images
        await axios(`/cps/app/catalog/${catalogID}/${lotID}/resequenceimages`);
        const lotImageResponse = await axios(
          `/cps/app/catalog/${catalogID}/${lotID}/house/${houseID}/getimageurls`
        );
        const lotImages = get(lotImageResponse, `data.data[${lotID}]`, []);

        // Re-render!
        this.setState({ lotImages, isDeletingFile: false });
      } catch {
        this.setState({ fileDeleteError: true, isDeletingFile: false });
      }
    }
  };

  /**
   * Unlike step4 - when a file is complete - we need to add it to the lotImages
   * JSON map which represents files that have already been uploaded.
   *
   * So this function removes the file when complete from the files array, and
   * updates lotImages
   */
  handleFileComplete = (imageURL, imageSeq) => {
    const lotImages = { ...this.state.lotImages };
    const files = [...this.state.files];

    const fileIndex = files.findIndex(file => file.seqNumber === imageSeq);
    files.splice(fileIndex, 1);

    lotImages[imageSeq] = imageURL;

    this.setState({ files, lotImages });
  };

  /**
   * Triggers when a user drops or selects files on the dropzone.
   *
   * In step4 we need to handle this differently due to lot # / inventory # constraints
   */
  handleFileDrop = files => {
    /**
     * Verify the user is connected to the internet before uploading files
     * to mitigate connection and timeout issues
     */
    if (!navigator.onLine) {
      this.setState({ offlineError: true });
      return;
    }

    const { lotImages } = this.props;
    const existingFiles = [...this.state.files];

    // Assign a sequence number - which allows us to perform the splice in handleFileComplete
    let index = 1;
    /**
     * ESLint thinks file is undefined - but for loop is preferred here
     * to be consistent with Step4.js
     */
    // eslint-disable-next-line
    for (const file of files) {
      file.error = {};
      file.hasError = false;
      file.isComplete = false;
      file.isSelected = false;
      file.photoID = null;
      file.seqNumber = Object.keys(lotImages).length + existingFiles.length + index;
      index += 1;
    }

    this.setState({ files: [...existingFiles, ...files], offlineError: false });
  };

  handleFileDropRejected = files => {
    const {
      match: {
        params: { type }
      }
    } = this.props;

    /**
     * ESLint thinks file is undefined - but for loop is preferred here
     * to be consistent with Step4.js
     */
    // eslint-disable-next-line
    for (const file of files) {
      file.error = imageUploadErrors('type', type);
      file.isComplete = true;
      file.isSelected = false;
      file.hasError = true;
    }

    const existingFiles = [...this.state.files];
    this.setState({ files: [...existingFiles, ...files] });
  };

  render() {
    const {
      match: {
        params: { catalogID, lotNumber }
      },
      lotData,
    } = this.props;

    const { files, fileDeleteError, isDeletingFile, lotImages, offlineError } = this.state;

    return (
      <div className="mt-4">
        <Row>
          {/* Start left column */}
          <Col xs={8}>
            <strong>Upload Images</strong>
            <p className="mb-2">
              1. Drag &amp; drop images from your computer in the uploader box below (or click the
              &quot;choose a file(s) from your computer&quot; link to find your file)
            </p>
            <p className="mb-2">2. Correct Errors if shown.</p>
            <p>
              Note: any images that contain errors will not be uploaded or saved after clicking
              &quot;Save &amp; Return to Summary&quot;. To reorder, drag and drop images into place.
            </p>

            {offlineError && (
              <div className="mt-3">
                <InlineAlert baseText={offlineErrorText} showPhones={false} />
              </div>
            )}

            <Step4Dropzone
              acceptedFileList=".jpg,.jpeg,.png"
              handleFileDrop={this.handleFileDrop}
              handleFileDropRejected={this.handleFileDropRejected}
            />
          </Col>
          {/* Start right column */}
          <Col xs={4}>
            <strong>Image Requirements</strong>
            <p>Images must end in .JPG, .JPEG &amp; .PNG (Do NOT use .GIF)</p>

            <Row>
              <Col>
                <strong>Max. Size</strong>
              </Col>
            </Row>

            <Row>
              <Col>
                <p>up to 10MB</p>
              </Col>
            </Row>
          </Col>
        </Row>

        {fileDeleteError && (
          <div className="mt-3">
            <InlineAlert baseText={fileDeleteErrorText} />
          </div>
        )}

        <AddLotImageContainer
          abortFile={this.abortFile}
          addFileError={this.addFileError}
          catalogID={catalogID}
          files={files}
          handleFileComplete={this.handleFileComplete}
          handleDeleteFile={this.handleDeleteFile}
          isDeletingFile={isDeletingFile}
          lotImages={lotImages}
          lotNumber={lotNumber}
          lotData={lotData}
        />
      </div>
    );
  }
}

export default withRouter(AddLotDropzone);
